import React, {useRef, useState} from 'react'
import classnames from 'classnames'
import emailjs from '@emailjs/browser'
import {toast} from "react-toastify"

import CalculateIcon from '@mui/icons-material/Calculate'
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import EditLocationIcon from "@mui/icons-material/EditLocation"
import PersonIcon from "@mui/icons-material/Person"
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined"
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined"

import './LandingPage.scss'
import {useThemeStore} from "../../stores/themeStore"

// Same wordmark as the login page, inverted to white in the SCSS.
const TRUEDESKING_LOGO = "https://res.cloudinary.com/hgjsxpz9b/image/upload/v1765310173/Asset_4_4x-8_teyqav.png"

// Images are hosted on Cloudinary, never bundled. App shots use made-up sample data.
const CLOUDINARY = "https://res.cloudinary.com/hgjsxpz9b/image/upload"
// Recordings are uploaded as video; w_960 + .gif makes Cloudinary serve an animated GIF (~4 MB).
const CLOUDINARY_GIF = "https://res.cloudinary.com/hgjsxpz9b/video/upload/w_960"
const CALCULATE_FLOW = `${CLOUDINARY_GIF}/v1791400344/calculator-flow-light_esuwqy.gif`

// Light mode except Live Inventory, which shows the dark theme.
const CRM_PIPELINE_SHOT = `${CLOUDINARY}/v1791396682/crm-pipeline_vlctm1.webp`
const LIVE_INVENTORY_SHOT = `${CLOUDINARY}/v1791396682/live-inventory_cqdl5w.webp`

const SIMPLICITY_ICONS = [CalculateIcon, RequestQuoteOutlinedIcon, AccountBalanceIcon, LocalOfferIcon, EditLocationIcon, PersonIcon]

// In deal order: compare, adjust, then the shared pricing behind it.
const FEATURES = [
    {
        label: 'Compare',
        title: 'Recalculate and compare lenders.',
        image: `${CLOUDINARY_GIF}/v1791400344/compare-quotes-light_mlses0.gif`,
        alt: 'Recalculating a quote and comparing lenders',
        text: 'After calculating a quote, you can easily recalculate it and compare lenders, for both financing and purchases.',
    },
    {
        label: 'Rebates, Fees & Taxes',
        title: 'Every rebate, fee and tax, adjustable.',
        image: `${CLOUDINARY}/v1791400344/rebates-fees-taxes-light_p2q5wl.webp`,
        alt: 'Editing rebates, fees and taxes on a quote',
        text: 'All rebates, fees and taxes can be easily adjusted. Brokers can add or remove custom rebates, edit fee amounts, and cap or pay any of them upfront.',
    },
    {
        label: 'Pricing Index',
        title: 'Your pricing, shared across the team.',
        image: `${CLOUDINARY}/v1791400344/pricing-index-light_rux4v6.webp`,
        alt: 'The Pricing Index page',
        text: "Easily store MSRP, invoice, profit and any other information you get from dealerships. Now you're one click away from the latest quotes.",
    },
]

export const LandingPage = () => {
    const theme = useThemeStore((state) => state.theme)
    const form = useRef()
    const [activeFeature, setActiveFeature] = useState(0)
    const featureTabs = useRef([])

    // Arrow keys move between feature tabs (WAI-ARIA tabs pattern).
    const onFeatureKeyDown = (e) => {
        const step = {ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1}[e.key]
        if (!step) return
        e.preventDefault()
        const next = (activeFeature + step + FEATURES.length) % FEATURES.length
        setActiveFeature(next)
        featureTabs.current[next]?.focus()
    }
    const feature = FEATURES[activeFeature]

    const sendEmail = (e) => {
        e.preventDefault()

        emailjs
            .sendForm('service_rwd4ja4', 'template_x0owgom', form.current, {
                publicKey: 'Ve1AbbNKoHOpGbiwp',
            })
            .then(
                () => {
                    toast.success("Success. Someone will reach out to you shortly.")
                },
                () => {
                    toast.error("Something went wrong, please try again.")
                },
            )
    }

    return (
        <div id={"LandingPage"} data-theme={theme}>
            <header className={'lp-hero'}>
                <nav className={'lp-nav'}>
                    <img className={'lp-logo'} src={TRUEDESKING_LOGO} alt={'TrueDesking'}/>
                    <a href="/login" className={'lp-btn lp-btn-primary'}>Sign In</a>
                </nav>

                <div className={'lp-container'}>
                    <div className={'lp-hero-content'}>
                        <h1>Desking for brokers,<br/>by brokers.</h1>
                        <p className={'lp-hero-text'}>
                            Auto brokers have been using outdated technology for far too long.
                            TrueDesking is the modern web app we've come to expect.
                        </p>
                        <a href="#one" className={'lp-btn lp-btn-outline'}>Learn More</a>
                        <div className={'lp-hero-line'} aria-hidden={true}/>
                    </div>
                </div>
            </header>

            <section id="one" className={'lp-section'}>
                <div className={'lp-container lp-split'}>
                    <div className={'lp-split-text'}>
                        <p className={'lp-eyebrow'}>Calculator</p>
                        <h2>Calculate leases in seconds.</h2>
                        <p>
                            Our lease calculator is as simple as it looks. Just select a car and set pricing,
                            and TrueDesking pulls quotes for all lenders with multiple terms for any location.
                        </p>
                    </div>
                    <img
                        className={'lp-shot'}
                        src={CALCULATE_FLOW}
                        alt={'The TrueDesking calculator pulling quotes for every lender and term'}
                    />
                </div>
            </section>

            <section id="simplicity" className={'lp-section lp-section-alt'}>
                <div className={'lp-container lp-split'}>
                    <div className={'lp-icon-grid'} aria-hidden={true}>
                        {SIMPLICITY_ICONS.map((Icon, i) => (
                            <div key={i} className={'lp-icon-tile'}><Icon/></div>
                        ))}
                    </div>
                    <div className={'lp-split-text'}>
                        <p className={'lp-eyebrow'}>Why TrueDesking</p>
                        <h2>Simplicity is the name of the game.</h2>
                        <p>
                            Outdated desking tools are bloated with too many inputs, and most are an eyesore to
                            look at. TrueDesking simplifies its interface to show only the most relevant
                            information, making it a pleasure to use.
                        </p>
                        <p>
                            Navigating TrueDesking is smooth and intuitive. Pages and features are one click
                            away at all times, without opening new pages and endless loading.
                        </p>
                        <p>
                            TrueDesking stores all relevant data so you don't have to re-enter information every
                            time. Quotes, pricing information, and locations for tax purposes are all saved and
                            at your fingertips.
                        </p>
                        <p>
                            Pricing information is stored across your company's account, so you avoid duplicate
                            information. Once MSRP, invoice and profit data is entered on the pricing page, it's
                            shared across your organization seamlessly.
                        </p>
                    </div>
                </div>
            </section>

            <section id="features" className={'lp-section'}>
                <div className={'lp-container'}>
                    <div className={'lp-section-header'}>
                        <p className={'lp-eyebrow'}>Features</p>
                        <h2>Feature rich at an unbeatable price.</h2>
                        <p>New applications are easier to develop, and we pass those savings on to you.</p>
                    </div>
                    <div className={'lp-feature-tabs'}>
                        <div role={'tablist'} aria-label={'Features'} aria-orientation={'vertical'} className={'lp-feature-list'} onKeyDown={onFeatureKeyDown}>
                            {FEATURES.map((item, i) => {
                                const isActive = i === activeFeature
                                return (
                                    <button
                                        key={item.label}
                                        ref={(el) => { featureTabs.current[i] = el }}
                                        type={'button'}
                                        role={'tab'}
                                        id={`lp-feature-tab-${i}`}
                                        aria-selected={isActive}
                                        aria-controls={'lp-feature-panel'}
                                        tabIndex={isActive ? 0 : -1}
                                        className={classnames('lp-feature-tab', {active: isActive})}
                                        onClick={() => setActiveFeature(i)}
                                    >
                                        <span className={'lp-eyebrow'}>{String(i + 1).padStart(2, '0')} · {item.label}</span>
                                        <span className={'lp-feature-title'}>{item.title}</span>
                                        {isActive && <span className={'lp-feature-text'}>{item.text}</span>}
                                    </button>
                                )
                            })}
                        </div>
                        <div id={'lp-feature-panel'} role={'tabpanel'} aria-labelledby={`lp-feature-tab-${activeFeature}`} className={'lp-window'}>
                            <div className={'lp-window-bar'} aria-hidden={true}><span/><span/><span/></div>
                            <img key={feature.image} src={feature.image} alt={feature.alt} loading={'lazy'}/>
                        </div>
                    </div>
                </div>
            </section>

            <section id="beyond" className={'lp-section lp-section-alt'}>
                <div className={'lp-container'}>
                    <div className={'lp-section-header'}>
                        <p className={'lp-eyebrow'}>Beyond the desk</p>
                        <h2>A CRM and live inventory, built in.</h2>
                        <p>Find the car, quote it and close the deal without leaving TrueDesking.</p>
                    </div>
                    <div className={'lp-card-grid lp-card-grid-wide'}>
                        <div className={'lp-card'}>
                            {CRM_PIPELINE_SHOT && (
                                <div className={'lp-window lp-card-window'}>
                                    <div className={'lp-window-bar'} aria-hidden={true}><span/><span/><span/></div>
                                    <img src={CRM_PIPELINE_SHOT} alt={'The CRM pipeline board'} loading={'lazy'}/>
                                </div>
                            )}
                            <div className={'lp-card-body'}>
                                <div className={'lp-card-title'}>
                                    <span className={'lp-card-icon'}><ViewKanbanOutlinedIcon/></span>
                                    <h3>CRM</h3>
                                </div>
                                <p>
                                    Move leads through your pipeline, keep each customer's quotes, vehicles and deal
                                    history on their profile, and track closed deals and profit in reports.
                                </p>
                            </div>
                        </div>
                        <div className={'lp-card'}>
                            {LIVE_INVENTORY_SHOT && (
                                <div className={'lp-window lp-card-window'}>
                                    <div className={'lp-window-bar'} aria-hidden={true}><span/><span/><span/></div>
                                    <img src={LIVE_INVENTORY_SHOT} alt={'Live dealer inventory search'} loading={'lazy'}/>
                                </div>
                            )}
                            <div className={'lp-card-body'}>
                                <div className={'lp-card-title'}>
                                    <span className={'lp-card-icon'}><DirectionsCarOutlinedIcon/></span>
                                    <h3>Live Inventory</h3>
                                    <span className={'lp-badge'}><span className={'lp-badge-dot'}/>Updated daily</span>
                                </div>
                                <p>
                                    Browse live new and used dealer inventory, refreshed every day. Pick a car and it
                                    drops straight into the calculator with the MSRP filled in.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="contact" className={'lp-section'}>
                <div className={'lp-container lp-split'}>
                    <div className={'lp-split-text'}>
                        <p className={'lp-eyebrow'}>Get started</p>
                        <h2>Want to see for yourself?</h2>
                        <p>Contact our team to get started, and someone will reach out to you shortly.</p>
                    </div>
                    <form ref={form} onSubmit={sendEmail} className={'lp-form'}>
                        <label htmlFor={'lp-name'}>Name</label>
                        <input id={'lp-name'} className={'lp-input'} type="text" name="from_name" placeholder={'Your name'}/>
                        <label htmlFor={'lp-email'}>Email</label>
                        <input id={'lp-email'} className={'lp-input'} type="email" name="user_email" placeholder={'you@dealer-group.com'}/>
                        <label htmlFor={'lp-message'}>Message</label>
                        <textarea id={'lp-message'} className={'lp-input'} name="message" placeholder={'Tell us about your desk'}/>
                        <button type="submit" className={'lp-btn lp-btn-primary'}>Send Message</button>
                    </form>
                </div>
            </section>

            <footer className={'lp-footer'}>
                <a href="mailto:support@truedesking.com">support@truedesking.com</a>
                <span className={'lp-footer-divider'} aria-hidden={true}/>
                <img className={'lp-logo'} src={TRUEDESKING_LOGO} alt={'TrueDesking'}/>
                <span className={'lp-footer-divider'} aria-hidden={true}/>
                <a href="tel:+13236005926">+1 (323) 600-5926</a>
            </footer>
        </div>
    )
}
